import { GalleryImage } from '../types/gallery';
import { apiFetch } from '@/lib/api';

export class ApiStorageService {
  private readonly baseUrl: string;
  private readonly clientIdKey = 'ima_client_id';

  constructor() {
    const env = import.meta.env as any;
    // First check for explicit VITE_API_URL
    const vURL = env?.VITE_API_URL as string | undefined;
    if (vURL) {
      this.baseUrl = vURL.replace(/\/$/, '');
      return;
    }
    // For development environments, use the API port from Vite config
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const isDevOrigin = origin.includes(':3000') || origin.includes(':3002') || origin.includes(':3032') || origin.includes(':3005') || origin.includes(':5173');
    if (isDevOrigin) {
      // Use the API port exposed by Vite config
      const apiPort = env?.VITE_API_PORT || '3001';
      this.baseUrl = `http://localhost:${apiPort}`;
    } else {
      // Production - use relative URLs
      this.baseUrl = '';
    }
  }

  private orgHeaders(extra?: Record<string,string>) {
    try {
      const orgId = localStorage.getItem('ima_active_org_id');
      return { ...(extra || {}), ...(orgId ? { 'X-Organization-Id': orgId } : {}) } as Record<string,string>;
    } catch {
      return extra || {};
    }
  }

  // Gallery methods (API-backed)
  async uploadGalleryImage(file: File, opts?: { title?: string; tags?: string[] }): Promise<GalleryImage> {
    const presignRes = await apiFetch(`${this.baseUrl}/api/gallery/presign`, {
      method: 'POST',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: file.type || 'application/octet-stream' }),
    });
    if (!presignRes.ok) throw new Error('Failed to presign gallery upload');
    const presigned = await presignRes.json();
    const { url, r2Key, id } = presigned.data || {};
    const putRes = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
    if (!putRes.ok) throw new Error('Upload to storage failed');
    const finRes = await apiFetch(`${this.baseUrl}/api/gallery/finalize`, {
      method: 'POST',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id,
        title: (opts?.title || file.name.replace(/\.[^.]+$/, '')),
        r2Key,
        originalFilename: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        tags: opts?.tags || [],
      }),
    });
    if (!finRes.ok) throw new Error('Finalize failed');
    const f = await finRes.json();
    return this.mapApiGalleryItem(f);
  }

  async listGalleryImages(q?: string): Promise<GalleryImage[]> {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      const response = await apiFetch(`${this.baseUrl}/api/gallery?${params.toString()}`, { headers: this.orgHeaders() });
      if (!response.ok) return [];
      const payload: { items: any[]; total: number } = await response.json();
      return (payload.items || []).map(this.mapApiGalleryItem);
    } catch (error) {
      console.error('Failed to load gallery images:', error);
      return [];
    }
  }

  async deleteGalleryImage(id: string): Promise<void> {
    const response = await apiFetch(`${this.baseUrl}/api/gallery/${id}`, { method: 'DELETE', headers: this.orgHeaders() });
    if (!response.ok) throw new Error(`Failed to delete gallery image: ${response.statusText}`);
  }

  async renameGalleryImage(id: string, title: string): Promise<void> {
    const response = await apiFetch(`${this.baseUrl}/api/gallery/${id}`, {
      method: 'PATCH',
      headers: this.orgHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ title }),
    });
    if (!response.ok) throw new Error(`Failed to rename gallery image: ${response.statusText}`);
  }

  async analyzeGalleryImage(id: string): Promise<void> {
    const response = await apiFetch(`${this.baseUrl}/api/gallery/${id}/analyze`, {
      method: 'POST',
      headers: this.orgHeaders({ 'Content-Type': 'application/json' }),
    });
    if (!response.ok) throw new Error(`Failed to start AI analysis: ${response.statusText}`);
  }

  // Org Backgrounds methods
  async listOrgBackgrounds(): Promise<any[]> {
    try {
      const response = await apiFetch(`${this.baseUrl}/api/org/backgrounds`, { headers: this.orgHeaders() });
      if (!response.ok) return [];
      const payload = await response.json();
      return payload?.data?.items || [];
    } catch (error) {
      console.error('Failed to load org backgrounds:', error);
      return [];
    }
  }

  // Utility methods
  private mapApiGalleryItem = (f: any): GalleryImage => ({
    id: f.id,
    filename: f.filename || f.originalName || f.title || f.stored_filename,
    originalName: f.originalName || f.original_filename || f.title || '',
    url: f.url,
    thumbnail: f.thumbnail || f.thumbnail_url || f.url,
    title: f.title,
    description: f.description || null,
    aiAnalyzedAt: f.aiAnalyzedAt ? new Date(f.aiAnalyzedAt) : null,
    metadata: {
      uploadedAt: new Date(f.metadata?.uploadedAt || f.created_at || Date.now()),
      size: f.metadata?.size ?? f.size ?? 0,
      dimensions: f.metadata?.dimensions ?? { width: f.width ?? 0, height: f.height ?? 0 },
      tags: f.metadata?.tags ?? f.tags ?? [],
      category: 'gallery',
      mimeType: f.mimeType || f.mime_type,
    },
  });
} 